<script setup lang="ts">
import { onBeforeMount, ref, computed, toRefs } from 'vue';
import Chip from 'primevue/chip';
import CheckboxForm from '@/components/Forms/CheckboxForm';
import { useServicesStore, useAuthStore } from '@/store';
import { PERMISSIONS } from '@/enum/permissions';
import { can } from '@/composables';
import { ServiceInterface } from '@/types';

interface Props {
  organizationId: number;
}

const props = defineProps<Props>();
const servicesStore = useServicesStore();
const authStore = useAuthStore();
const { organizationId } = toRefs(props);
const permissionToUpdate = can([PERMISSIONS.ORGANIZATION_SYNC_SERVICES]);

const dirty = ref(false);

// eslint-disable-next-line
// @ts-ignore
const activeEventTypes = computed<ServiceInterface[]>(() =>
  !authStore.currentInstanceIsMasterTenant
    ? servicesStore.services?.filter((service) => service.checked)
    : servicesStore.services,
);

const onSave = async () => {
  const result = await servicesStore.saveServices(organizationId.value);
  result === 200
    ? servicesStore.doToast('Services have been saved', 'Saved')
    : servicesStore.doToast('Services have not been saved', 'Error', true);

  dirty.value = false;
};

const onUpdate = (id: string, checked: boolean) => {
  if (!dirty.value) {
    dirty.value = true;
  }
  servicesStore.updateService({ id, checked });
};

const onCancel = () => {
  dirty.value = false;
  servicesStore.getServices(organizationId.value);
};

onBeforeMount(() => {
  if (can([PERMISSIONS.ORGANIZATION_SERVICES_INDEX])) servicesStore.getServices(organizationId.value);
});

const icons: Record<string, string> = {
  'Caseload Management': 'settings',
  Consultation: 'assignment',
  Evaluation: 'assignment_turned_in',
  'IEP Meeting': 'assignment_ind',
  'Record Review': 'rate_review',
  Session: 'perm_contact_calendar',
  'Response to Intervention': 'support',
};
</script>

<template>
  <CheckboxForm
    v-if="!authStore.currentInstanceIsMasterTenant && permissionToUpdate"
    title="Event Types"
    :items="servicesStore.services"
    :update-item="onUpdate"
    :save="onSave"
    :loading="servicesStore.loading && permissionToUpdate"
    :cancel="onCancel"
    :dirty="dirty && permissionToUpdate"
    class-name="checkbox-form"
  />
  <Card v-else class="form-card p-fluid checkbox-form">
    <template #header>
      <h2>Event Types</h2>
    </template>
    <template #content>
      <div class="flex flex-column">
        <div v-if="servicesStore.loading">
          <Skeleton v-for="n in 5" :key="n" width="100%" height="24px" />
        </div>
        <div v-for="item of activeEventTypes" :key="item.id" class="mb-2">
          <Chip class="pl-0 pr-1">
            <Icon :name="icons[item.name]" size="text-base" class="ml-1" :style="`color: #${item.color}`" />
            <span class="p-chip-text ml-1 font-semibold" v-text="item.name" />
          </Chip>
        </div>
      </div>
    </template>
  </Card>
</template>

<style scoped lang="scss">
.checkbox-form {
  height: 100%;
  :deep(.p-chip) {
    background-color: $grayLight;
    height: 22px;
    border-radius: 4px;
    .p-chip-text {
      font-size: 14px;
    }
  }
}
</style>
